import React, { useContext, useEffect, useState } from 'react';
import {
	sendGaChangeEvent,
	sendGaFocusEvent,
	sendGaSuccessEvent,
} from './enrollment-ga-events';
import context from '../../../hoc/context/product/context';
import { enrollmentContext } from '../../../hoc/context/enrollment/enrollment-context';
import { DeepMap, FieldError, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IFormInput } from './enrollment.models';
import Tooltip from '../../../hoc/components/tooltip';
import RichText from '../../../hoc/components/rich-text';
import { RouteComponentProps } from '@gatsbyjs/reach-router';
import PatientInformation from '../components/patientInformation/patient-information';
import ExtraPatientInformation from '../components/extraPatientInformation/extra-patient-information';
import ContactInformation from '../components/contactInformation/contact-information';
import { TermsConditions } from '../components/termsAndConditions/terms-conditions';
import Button from '../../../hoc/components/button';
import { validations } from './validations';
import ErrorsDisplay from '../components/errors-display/errors-display';
import { CaregiverInformation } from '../components/caregiverInformation/caregiver-information';
import { PerformerConstants } from '../../../hoc/constants/performer.constants';
import { Communication } from '../components/communication/communication';
import {
	EventLabel,
	EventKeys,
	EventCategory,
	ErrorType,
} from '../../../../services/ga-enums-interfaces';
import withAnalytics, {
	AnalyticsProps,
} from '../../../hoc/components/analytics';
import AnalyticsFactory from '../../../../services/analytics-factory';

interface EnrollmentProps extends RouteComponentProps, AnalyticsProps {
	nextForm: () => void;
}

const Enrollment = ({
	nextForm,
	sendAnalyticsErrors,
}: EnrollmentProps): JSX.Element => {
	const [checkTerms, setCheckTerms] = useState(false);
	const [termsChecked, setTermsChecked] = useState(true);
	const ctxt = useContext(context);
	const enrollmentCtxt = useContext(enrollmentContext);
	const isEmpty = (inputObject: DeepMap<IFormInput, FieldError>) => {
		return Object.keys(inputObject).length === 0;
	};
	const formConfig = ctxt.json.getForm('patientDetails');
	const mainFormValidations = validations(ctxt, enrollmentCtxt);
	const analytics = AnalyticsFactory.createAnalytics('ga');

	const {
		register,
		errors,
		handleSubmit,
		formState,
		setValue,
		control,
		clearErrors,
	} = useForm<IFormInput>({
		resolver: yupResolver(mainFormValidations),
		mode: 'onBlur',
		reValidateMode: 'onBlur',
		defaultValues: enrollmentCtxt.getFormFieldsValues(),
		shouldFocusError: false,
	});
	const hookProps = { errors, register, formState };

	useEffect(() => {
		document.addEventListener('change', sendGaChangeEvent);
		return () => document.removeEventListener('change', sendGaChangeEvent);
		// Making a second useEffect with empty dependency array because both functions need
		// to return a clean-up function
	}, []);

	useEffect(() => {
		if (Object.keys(errors).length !== 0) {
			analytics.sendEvent({
				[EventKeys.EventCategory]: EventCategory.ProfileForm,
				[EventKeys.EventAction]: `${ctxt.json.pages.questions.next_button_label}`,
				[EventKeys.EventLabel]: EventLabel.Error,
				[EventKeys.ProfileForm]: EventLabel.Error,
			});
			sendAnalyticsErrors(errors, ErrorType.NormalError);
			showErrors();
		} else {
			enrollmentCtxt.input.isFormValid = true;
		}
	}, [errors]);

	const onTermsChecked = (checked: boolean, checkRequested: boolean) => {
		setTermsChecked(checked);
		setCheckTerms(false);
		if (checked && checkRequested) {
			onSubmit();
		}
	};

	const onSubmit = () => {
		if (isEmpty(formState.errors)) {
			if (!termsChecked) {
				setCheckTerms(true);
			} else {
				enrollmentCtxt.processFormFields();
				sendGaSuccessEvent(
					enrollmentCtxt,
					`${ctxt.json.pages.questions.next_button_label}`
				);
				nextForm();
			}
		}
	};

	const showErrors = () => {
		if (errors) {
			const el = document.querySelectorAll('p.text-error')[0];
			if (el) {
				el.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				});
			}
		}
	};

	return (
		<form
			className="w-full flex flex-col gap-24"
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={(e: React.KeyboardEvent) => {
				e.key === 'Enter' && e.preventDefault();
			}}
			data-testid="component-profile-form">
			<div className="w-full max-w-3xl mx-auto">
				{Object.keys(errors).length > 0 && <ErrorsDisplay errors={errors} />}
				<div className="flex flex-row items-center">
					<div className="text-2xl text-font">
						{formConfig?.title}
						<span className="inline-block">
							{formConfig?.help && (
								<Tooltip className="ml-2" fieldname={formConfig?.title}>
									<RichText html={formConfig.help} />
								</Tooltip>
							)}
						</span>
					</div>
				</div>
				{formConfig?.description && (
					<div className="mb-5 md:mb-9 text-font text-sm">
						<RichText html={formConfig.description} />
					</div>
				)}
				<PatientInformation
					hookProps={hookProps}
					control={control}
					setValue={setValue}
					clearErrors={clearErrors}
				/>
				{ctxt.json.getField('medication') && (
					<div className="mt-12">
						<ExtraPatientInformation
							hookProps={hookProps}
							control={control}
							clearErrors={clearErrors}
						/>
					</div>
				)}
				<div className="mt-12">
					<ContactInformation
						hookProps={hookProps}
						control={control}
						setValue={setValue}
						clearErrors={clearErrors}
					/>
				</div>
				{ctxt.json.getForm('caregiverDetails') &&
					enrollmentCtxt.input.performer === PerformerConstants.caregiver && (
						<div className="mt-24">
							<CaregiverInformation
								hookProps={hookProps}
								control={control}
								setValue={setValue}
								clearErrors={clearErrors}
							/>
						</div>
					)}
				<div className="mt-5">
					<Communication
						hookProps={hookProps}
						control={control}
						setValue={setValue}
						clearErrors={clearErrors}
					/>
				</div>
			</div>
			<TermsConditions
				onTermsChecked={onTermsChecked}
				checkTerms={checkTerms}
				hookProps={hookProps}
				control={control}
				onFieldFocus={(event: React.FocusEvent<HTMLInputElement>) => {
					//TODO: Use analytics props and remove onFieldFocus, to be done
					//during checkbox GA refactoring
					sendGaFocusEvent(event.target.name);
				}}
				setValue={setValue}
				clearErrors={clearErrors}
			/>
			<div id="bottom-anchor" className="flex justify-center w-full">
				<Button data-test-id="btn-enrollment">
					<span className="px-2">
						{ctxt.json.pages.questions.next_button_label}
					</span>
				</Button>
			</div>
		</form>
	);
};
//TODO: fix type error as params are missing
export default withAnalytics(Enrollment);
